import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, ShieldAlert, Database, HardDrive,
  Loader2, AlertTriangle, CheckCircle, XCircle, Download,
  Server, Clock, RefreshCw, FileArchive, Activity, Wifi,
  ChevronDown, ChevronUp, BookOpen, Play, TestTube,
} from "lucide-react";

const STATUS_COLORS = {
  healthy: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  degraded: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  down: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
  critical: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
  not_configured: { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", dot: "bg-gray-400" },
  unknown: { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", dot: "bg-gray-400" },
};

function ServiceCard({ name, icon: Icon, data }) {
  const status = data?.status || "unknown";
  const sc = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <div className={`rounded-lg border p-3 ${sc.bg} ${sc.border}`} data-testid={`dr-service-${name.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2 h-2 rounded-full ${sc.dot} ${status === "down" || status === "critical" ? "animate-pulse" : ""}`} />
        <Icon className={`w-3.5 h-3.5 ${sc.text}`} />
        <span className="text-xs font-bold">{name}</span>
        <Badge variant="outline" className={`text-[8px] h-4 ml-auto ${sc.text}`}>{status.toUpperCase()}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-3 text-[10px] text-muted-foreground">
        {Object.entries(data || {}).filter(([k]) => k !== "status" && k !== "error" && k !== "note").map(([k, v]) => (
          <div key={k} className="truncate">
            <span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span>{" "}
            <span className="font-mono font-bold">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
        {data?.error && <div className="col-span-2 text-red-600 truncate">{data.error}</div>}
        {data?.note && <div className="col-span-2 text-muted-foreground">{data.note}</div>}
      </div>
    </div>
  );
}

function RunbookScenario({ scenario }) {
  const [open, setOpen] = useState(false);
  const sevColor = scenario.severity === "critical" ? "text-red-600 bg-red-50 border-red-200"
    : scenario.severity === "high" ? "text-orange-600 bg-orange-50 border-orange-200"
    : "text-amber-600 bg-amber-50 border-amber-200";

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`runbook-${scenario.id}`}>
      <button className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/20 transition-colors" onClick={() => setOpen(!open)}>
        <ShieldAlert className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-bold flex-1">{scenario.title}</span>
        <Badge variant="outline" className={`text-[8px] h-4 border ${sevColor}`}>{scenario.severity}</Badge>
        <span className="text-[10px] text-muted-foreground font-mono">RTO: {scenario.rto}</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t bg-muted/10">
          <ol className="list-decimal list-inside space-y-1 mt-2">
            {scenario.steps.map((step, i) => (
              <li key={i} className="text-[11px] text-muted-foreground leading-relaxed">{step}</li>
            ))}
          </ol>
          <div className="text-[10px] p-2 bg-emerald-50 rounded border border-emerald-200">
            <strong className="text-emerald-700">Validacao:</strong> <span className="text-emerald-600">{scenario.validation}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function DRTab({ adminAxios }) {
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState(null);
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showCollections, setShowCollections] = useState(false);
  const [validating, setValidating] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, bRes, rRes] = await Promise.all([
        adminAxios.get("/dr/health"),
        adminAxios.get("/dr/backups"),
        adminAxios.get("/dr/runbook"),
      ]);
      setHealth(hRes.data);
      setBackups(bRes.data);
      setRunbook(rRes.data);
    } catch {
      toast.error("Erro ao carregar dados de DR");
    }
    setLoading(false);
  }, [adminAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const triggerBackup = async () => {
    setBackingUp(true);
    try {
      const res = await adminAxios.post("/dr/backups/trigger");
      toast.success(`Backup criado: ${res.data?.filename || "OK"} (${res.data?.size_mb || 0} MB)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no backup");
    }
    setBackingUp(false);
  };

  const testRestore = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAxios.post("/dr/restore/test");
      setTestResult(res.data);
      if (res.data.status === "success") toast.success("Teste de restore OK");
      else toast.warning(res.data.message || "Sem backups para testar");
    } catch (e) {
      toast.error("Erro no teste de restore");
    }
    setTesting(false);
  };

  const validateBackup = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await adminAxios.post("/dr/backups/validate");
      setValidationResult(res.data);
      toast.success(res.data.restore_test_status === "pass" ? "Backup validado!" : "Validacao parcial");
    } catch {
      toast.error("Erro na validacao");
    }
    setValidating(false);
  };

  const simulateFailover = async () => {
    setSimulating(true);
    setSimResult(null);
    try {
      const res = await adminAxios.post("/dr/simulate/failover");
      setSimResult(res.data);
      toast.success(res.data.overall === "pass" ? "Todos testes passaram!" : res.data.summary);
    } catch {
      toast.error("Erro na simulacao");
    }
    setSimulating(false);
  };

  if (loading && !health) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const hc = health?.checks || {};
  const overallColor = STATUS_COLORS[health?.status] || STATUS_COLORS.unknown;

  return (
    <div className="space-y-4" data-testid="dr-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-500/10"><Shield className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h2 className="font-black text-lg tracking-tight">Disaster Recovery</h2>
            <p className="text-[10px] text-muted-foreground">Backup, Restore, Health Checks & Runbooks</p>
          </div>
          <Badge variant="outline" className={`text-[10px] h-5 ${overallColor.text}`}>{(health?.status || "loading").toUpperCase()}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="gap-1 h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={triggerBackup} disabled={backingUp} data-testid="trigger-backup-btn">
            {backingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}Backup Agora
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-7 text-[10px]" onClick={validateBackup} disabled={validating} data-testid="validate-backup-btn">
            {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}Validar Backup
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-7 text-[10px]" onClick={testRestore} disabled={testing} data-testid="test-restore-btn">
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}Testar Restore
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-7 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50" onClick={simulateFailover} disabled={simulating} data-testid="simulate-failover-btn">
            {simulating ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}Simular Failover
          </Button>
          <Button size="sm" variant="ghost" className="gap-1 h-7 text-[10px]" onClick={fetchData} data-testid="refresh-dr-btn">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Test Restore Result */}
      {testResult && (
        <div className={`p-3 rounded-lg border text-xs ${testResult.status === "success" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="test-result">
          <div className="flex items-center gap-2">
            {testResult.status === "success" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
            <span className="font-bold">{testResult.status === "success" ? "Restore verificado" : testResult.message}</span>
          </div>
          {testResult.backup_file && (
            <div className="mt-1 text-muted-foreground">
              Arquivo: <strong className="font-mono">{testResult.backup_file}</strong> | {testResult.size_mb} MB | {testResult.files_count} arquivos
            </div>
          )}
        </div>
      )}

      {/* Failover Simulation Result */}
      {simResult && (
        <div className={`p-3 rounded-lg border text-xs ${simResult.overall === "pass" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="sim-result">
          <div className="flex items-center gap-2 mb-2">
            {simResult.overall === "pass" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
            <span className="font-bold">Failover Simulation: {simResult.summary}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
            {(simResult.tests || []).map((t, i) => (
              <div key={i} className={`flex items-center gap-1.5 p-1.5 rounded border ${t.status === "pass" ? "bg-emerald-50/50 border-emerald-100" : t.status === "skip" ? "bg-gray-50 border-gray-200" : "bg-red-50/50 border-red-100"}`}>
                {t.status === "pass" ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : t.status === "skip" ? <Activity className="w-3 h-3 text-gray-400" /> : <XCircle className="w-3 h-3 text-red-500" />}
                <span className="text-[10px] font-medium">{t.name}</span>
                {t.latency_ms && <span className="text-[9px] text-muted-foreground ml-auto font-mono">{t.latency_ms}ms</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backup Validation Result */}
      {validationResult && (
        <div className={`p-3 rounded-lg border text-xs ${validationResult.restore_test_status === "pass" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="validation-result">
          <div className="flex items-center gap-2">
            {validationResult.restore_test_status === "pass" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
            <span className="font-bold">Validacao: {validationResult.restore_test_status?.toUpperCase()}</span>
            <span className="text-muted-foreground ml-2">{validationResult.backup_id} | {validationResult.size_mb} MB | {validationResult.files_valid}/{validationResult.files_total} arquivos validos</span>
          </div>
        </div>
      )}

      {/* Service Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <ServiceCard name="MongoDB" icon={Database} data={hc.mongodb} />
        <ServiceCard name="Replica Set" icon={Server} data={hc.replica_set} />
        <ServiceCard name="Redis" icon={Wifi} data={hc.redis} />
        <ServiceCard name="Disco" icon={HardDrive} data={hc.disk} />
      </div>

      {/* Collections */}
      {hc.collections && (
        <Card>
          <CardContent className="p-4">
            <button className="flex items-center gap-2 w-full text-left" onClick={() => setShowCollections(!showCollections)} data-testid="toggle-collections">
              <Database className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">
                Colecoes MongoDB ({hc.collections.total})
              </span>
              {showCollections ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showCollections && hc.collections.counts && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-1.5">
                {Object.entries(hc.collections.counts).sort(([,a],[,b]) => b - a).map(([col, count]) => (
                  <div key={col} className="text-[10px] p-1.5 border rounded bg-muted/20 flex justify-between">
                    <span className="truncate font-medium">{col}</span>
                    <span className="font-mono text-muted-foreground ml-1">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Backups */}
      <Card data-testid="backups-section">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileArchive className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">Backups</span>
            <Badge variant="outline" className="text-[8px] h-4">
              GCS: {backups?.gcs_configured ? "Configurado" : "Nao configurado"}
            </Badge>
          </div>
          {(backups?.local_backups || []).length > 0 ? (
            <div className="space-y-1">
              {backups.local_backups.map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] p-1.5 border rounded">
                  <FileArchive className="w-3 h-3 text-blue-500" />
                  <span className="font-mono font-medium flex-1 truncate">{b.filename}</span>
                  <span className="text-muted-foreground">{b.size_mb} MB</span>
                  <span className="text-muted-foreground font-mono">{b.created_at?.slice(0, 19)}</span>
                  <Badge variant="outline" className="text-[7px] h-3.5">{b.location}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[10px] text-muted-foreground py-4">Nenhum backup local encontrado</p>
          )}
          {(backups?.db_backup_log || []).length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-muted-foreground mb-1.5">Log de Backups</p>
              <div className="space-y-1">
                {backups.db_backup_log.slice(0, 10).map((log, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px] p-1.5 border rounded">
                    {log.status === "success" ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                    <span className="font-mono flex-1 truncate">{log.filename}</span>
                    <span className="text-muted-foreground">{log.size_mb} MB</span>
                    <span className="text-muted-foreground">{log.triggered_by}</span>
                    <span className="text-muted-foreground font-mono">{log.timestamp?.slice(0, 19)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DR Runbook */}
      {runbook && (
        <Card data-testid="runbook-section">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">DR Runbook</span>
              <div className="flex gap-3 text-[10px] text-muted-foreground">
                <span>RTO: <strong>{runbook.rto_target}</strong></span>
                <span>RPO: <strong>{runbook.rpo_target}</strong></span>
                <span>Atualizado: <strong>{runbook.last_updated}</strong></span>
              </div>
            </div>
            <div className="space-y-2">
              {(runbook.scenarios || []).map(s => <RunbookScenario key={s.id} scenario={s} />)}
            </div>
            {runbook.automated_protections && (
              <div className="mt-4">
                <p className="text-[10px] font-bold text-muted-foreground mb-1.5">Protecoes Automaticas Ativas</p>
                <div className="space-y-1">
                  {runbook.automated_protections.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px]">
                      <ShieldCheck className="w-3 h-3 text-emerald-500" />
                      <span className="text-muted-foreground">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
